import { MILLISECONDS } from './time';

export const INACTIVITY_LOCK_PIN_LENGTH = 6;

export const INACTIVITY_LOCK_MAX_UNLOCK_ATTEMPTS = 5;

/**
 * Clinic-configurable timeout values, in minutes. Server validates against
 * this exact list and rejects anything else; UI renders a Select from this
 * list. `0` is the "Never" sentinel — the clinic is opting out of the in-app
 * lock and accepting responsibility for OS-level workstation lock (the
 * position Jane and ChiroTouch take by default). All other values are
 * tightest control first, max last.
 */
export const INACTIVITY_LOCK_TIMEOUT_OPTIONS_MINUTES = [
  0, 5, 15, 30, 60, 120, 240, 480, 720,
] as const;

export type InactivityLockTimeoutMinutes = (typeof INACTIVITY_LOCK_TIMEOUT_OPTIONS_MINUTES)[number];

/**
 * Sentinel value meaning "lock disabled" — the clinic has explicitly opted
 * out. Distinct from `undefined` (no preference set → falls through to default).
 */
export const INACTIVITY_LOCK_DISABLED_MINUTES = 0;

/**
 * Default for clinics WITH the EHR — opt-out (no in-app lock). Clinics turn
 * the lock on deliberately; until they do, the OS-level workstation lock is
 * the control. "Never" is the safe default so the lock never surprises a
 * clinic that hasn't configured it.
 */
export const INACTIVITY_LOCK_EHR_DEFAULT_MINUTES: InactivityLockTimeoutMinutes =
  INACTIVITY_LOCK_DISABLED_MINUTES;

/**
 * Default for clinics WITHOUT the EHR — opt-out (no in-app lock). These
 * clinics aren't storing clinical PHI through Chiroup, so the OS-level
 * workstation lock is the appropriate control.
 */
export const INACTIVITY_LOCK_NON_EHR_DEFAULT_MINUTES: InactivityLockTimeoutMinutes =
  INACTIVITY_LOCK_DISABLED_MINUTES;

/**
 * Resolve the prefill / fallback default for a clinic that hasn't saved an
 * `inactivityLockTimeoutMinutes` value yet. Branches on EHR because clinical
 * PHI raises the floor — non-EHR clinics opt out by default.
 */
export const getInactivityLockDefaultMinutes = (
  hasEhr: boolean | undefined | null
): InactivityLockTimeoutMinutes =>
  hasEhr ? INACTIVITY_LOCK_EHR_DEFAULT_MINUTES : INACTIVITY_LOCK_NON_EHR_DEFAULT_MINUTES;

/**
 * PINs trivially guessable on a 6-digit keypad. Shared between the UI (which
 * rejects them before submit) and the backend (defense in depth so a direct
 * API call can't persist one). Limited to the most obvious — full
 * dictionary-based strength is overkill for a "convenience unlock" PIN.
 */
export const INACTIVITY_LOCK_WEAK_PINS: ReadonlySet<string> = new Set([
  '000000',
  '111111',
  '222222',
  '333333',
  '444444',
  '555555',
  '666666',
  '777777',
  '888888',
  '999999',
  '123456',
  '234567',
  '345678',
  '456789',
  '654321',
  '987654',
]);

export const isWeakLockPin = (pin: string): boolean => INACTIVITY_LOCK_WEAK_PINS.has(pin);

/**
 * Map a stored ClinicExtendedSettings value to a runtime timeout. Returns
 * `null` when the lock is disabled (configured `0`, or the EHR-aware default
 * for an unset/invalid value happens to be `0`). Otherwise returns the
 * timeout in milliseconds. Invalid/missing values fall through to the
 * EHR-aware default — never a longer value than the clinic's baseline.
 */
export const resolveInactivityTimeoutMs = (
  raw: unknown,
  hasEhr: boolean | undefined | null
): number | null => {
  const n = typeof raw === 'string' ? Number(raw) : typeof raw === 'number' ? raw : NaN;
  const minutes =
    Number.isFinite(n) && (INACTIVITY_LOCK_TIMEOUT_OPTIONS_MINUTES as readonly number[]).includes(n)
      ? (n as InactivityLockTimeoutMinutes)
      : getInactivityLockDefaultMinutes(hasEhr);

  if (minutes === INACTIVITY_LOCK_DISABLED_MINUTES) return null;

  return MILLISECONDS.inMinutes(minutes);
};

/**
 * Warning lead time: how long before the lock fires the heads-up toast shows.
 * Scales with the configured timeout but always at least 30s and never more
 * than 1 min — so a 5-min timeout still gives a meaningful warning and a
 * 12-hour timeout doesn't try to warn hours in advance.
 */
export const resolveInactivityWarningMs = (timeoutMs: number): number => {
  const oneMinute = MILLISECONDS.inMinutes(1);
  const thirtySeconds = MILLISECONDS.inSeconds(30);

  return Math.max(thirtySeconds, Math.min(oneMinute, Math.floor(timeoutMs / 5)));
};
